import React, { Component } from 'react';
import pure from 'recompose/pure';
import { Cart } from '@onehope/cart';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql, QueryRenderer } from 'react-relay';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { ApolloProvider, ApolloConsumer } from '@apollo/react-hooks';

/* Mutations */
import CartAddMutation from '../../../mutationsModern/Cart/CartV2AddMutation';
import CartDeleteMutation from '../../../mutationsModern/Cart/CartV2DeleteMutation';
import CartTogglePCMembershipMutation from '../../../mutationsModern/Cart/CartTogglePCMembershipMutation';
import CartToggleAutoshipMutation from '../../../mutationsModern/Cart/CartV2ToggleAutoshipMutation';
import CartAdjustAutoshipMutation from '../../../mutationsModern/Cart/CartV2AdjustAutoshipMutation';
import CartPromoCodeDeleteMutation from '../../../mutationsModern/Cart/CartV2PromoCodeDeleteMutation';
import CartUpdatePromoCodesMutation from '../../../mutationsModern/Cart/CartV2UpdatePromoCodes';
import CartApplyPointsMutation from '../../../mutationsModern/Cart/CartApplyPointsMutation';

/* Helpers */
import { trackEvent } from '../../../utils/tracking';
import { client } from '../../../../monorepo/apollo/client';
import { urlSquareShopProduct } from '../../../../server/utils/urlConstants';
import {
  urlCheckout,
  urlHopecommerce,
} from '../../../../server/utils/routeConstants';
import {
  getPwpProduct,
  shouldDisplayPwPDialog,
  getPwpConfigItem,
  getOldRewardsCoupon,
  getRewardsFlag,
} from '../../../utils/helpers';
import { pwpConfig } from './pwpConfig';
import { yotpoRedemptionId } from '../../../marketplace/env';
import { LaunchDates } from '../../../../server/utils/launchDateConstants';

const seasonalWineClubDateGate = new Date() > LaunchDates.seasonalWineClub;

const propTypes = {
  viewer: PropTypes.object.isRequired,
  relay: PropTypes.object.isRequired,
  containerWidth: PropTypes.number.isRequired,
  handleRouteChange: PropTypes.func.isRequired,
  cartNavOpen: PropTypes.bool.isRequired,
  handleCheckoutPage: PropTypes.func.isRequired,
  handleCartToggle: PropTypes.func.isRequired,
  handleCartClose: PropTypes.func.isRequired,
  location: PropTypes.string.isRequired,
  handleAuthentication: PropTypes.func.isRequired,
  handleCartHotSwap: PropTypes.func.isRequired,
};

class CartRightNavComponent extends Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      cartMutationLoading: false,
      applyRewardsToCartMutationLoading: false,
      cartPromoCodeUpdateMutationLoading: false,
    };
  }

  initiateCheckout = e => {
    e.preventDefault();
    const {
      handleCheckoutPage,
      viewer: { user },
    } = this.props;
    const { cart } = user;

    trackEvent({
      user,
      event: 'Checkout Started',
      properties: {
        cart,
      },
    });
    handleCheckoutPage();
  };

  getCartItem = ({ user, product }) => {
    return user.cart.entries.edges.find(
      ({ node: cartEntry }) =>
        cartEntry.product.productId === product.productId,
    );
  };

  onCartAddMutationComplete = () => {
    this.setState({ cartMutationLoading: false });
    const {
      togglePwpDialogOpen,
      flags,
      showPwpDialog,
      togglePwpDialogClosed,
    } = this.props;
    const { productsBulk, user } = this.props.viewer;
    const { cartV2: cart } = user;
    const { discountQualifiedCartTotal } = cart;
    const isShop =
      !window.location.pathname.includes('myshop') &&
      window.location.pathname.includes('shop');
    if (isShop && discountQualifiedCartTotal >= 6) {
      const pwpProduct = getPwpProduct(flags, productsBulk);
      if (pwpProduct) {
        const cartItem = !!this.getCartItem({ user, product: pwpProduct });
        if (!cartItem) {
          const showDisplayPwPDialog = shouldDisplayPwPDialog({
            viaQualifiedCartTotal: discountQualifiedCartTotal,
          });
          if (showDisplayPwPDialog) {
            togglePwpDialogOpen();
          }
        } else {
          const pwpConfigItem = getPwpConfigItem(pwpProduct.productId);
          if (
            showPwpDialog &&
            pwpConfigItem &&
            pwpConfigItem.capQuantity === 1
          ) {
            togglePwpDialogClosed();
          }
        }
      }
    }
  };

  onCartAddMutationError = () => {
    this.setState({ cartMutationLoading: false });
  };

  onCartDeleteMutationComplete = () => {
    this.setState({ cartMutationLoading: false });
  };

  handleCartDelete = (user, product) => {
    const { environment } = this.props.relay;
    this.setState({ cartMutationLoading: true });
    const { id } = product;
    CartDeleteMutation.commit({
      environment,
      id,
      productId: id,
      conciergeCustomerAccountId: user.conciergeCustomerAccountId,
      onCompleted: this.onCartDeleteMutationComplete,
      onError: this.onCartAddMutationError,
    });
  };

  handleCartAdd = (user, cartItems, qty, qtyChange, product) => {
    const quantity = qtyChange + qty;
    const { id, productId } = product;
    const { conciergeCustomerAccountId } = user;
    const { environment } = this.props.relay;
    this.setState({ cartMutationLoading: true });
    CartAddMutation.commit({
      environment,
      id,
      quantity,
      productId,
      cartItems,
      conciergeCustomerAccountId,
      onCompleted: this.onCartAddMutationComplete,
      onError: this.onCartAddMutationError,
    });
  };

  togglePCEnroll = () => {
    const { environment } = this.props.relay;
    return CartTogglePCMembershipMutation.commit({ environment });
  };

  togglePCAutoship = () => {
    const { environment } = this.props.relay;
    return CartToggleAutoshipMutation.commit({
      environment,
    });
  };

  setAutoshipCount = (cart, autoShipIntervalCount) => {
    const { environment } = this.props.relay;
    return CartAdjustAutoshipMutation.commit({
      autoShipIntervalUnit: 'month',
      autoShipIntervalCount,
      environment,
    });
  };

  handleDeleteGrapefulPromo = (setIsNotEditing, cb, setErrors) => {
    const {
      relay: { environment },
    } = this.props;
    const relayArgsObj = {
      environment,
      promoCode: 'GRAPEFULCLUB',
      onCompleted: async () => {
        if (setIsNotEditing) setIsNotEditing();
        if (cb) cb();
        if (setErrors) setErrors({});
        // if user has PC subscription in cart, treat this as intention to remove that as well
        // (why would you buy it and immediately not use it?)
        await CartTogglePCMembershipMutation.commit({ environment });
      },
      onError: trans => {
        console.log('TRANS error: ', trans);
      },
    };
    CartPromoCodeDeleteMutation.commit(relayArgsObj);
  };

  handleDeletePromoCode = (promoCode, setIsNotEditing, cb, setErrors) => {
    const { environment } = this.props.relay;
    const relayArgsObj = {
      environment,
      promoCode: promoCode.code,
      onCompleted: response => {
        if (setIsNotEditing) {
          setIsNotEditing();
        }
        if (setErrors) {
          setErrors({});
        }
        return;
      },
      onError: trans => {
        console.log('error', trans);
      },
    };
    return CartPromoCodeDeleteMutation.commit(relayArgsObj);
  };

  handlePromoCodeUpdate = (promoCode, isUserPc, setErrors, resetForm) => {
    const {
      relay: { environment },
      flags,
    } = this.props;
    const rewardPoints = getRewardsFlag(flags);
    this.setState({ cartPromoCodeUpdateMutationLoading: true });
    const promoCodes = [promoCode.toUpperCase()];
    const relayArgsObj = {
      environment,
      promoCodes,
      noGiftCards: !!rewardPoints,
      key: undefined,
      onCompleted: (response, error) => {
        this.setState({ cartPromoCodeUpdateMutationLoading: false });
        console.log('response promo', response);
        if (error) {
          console.log('error in CartUpdatePromoCodesMutation', error);
          return setErrors({ promoCode: error[0].message });
        }
        if (resetForm) {
          resetForm();
        }
        return setErrors({});
      },
      onError: transaction => {
        this.setState({ cartPromoCodeUpdateMutationLoading: false });
        console.log('TRANS in error', transaction);
        if (transaction.graphQLErrors[0].message) {
          return setErrors({ promoCode: transaction.graphQLErrors[0].message });
        }
      },
    };
    CartUpdatePromoCodesMutation.commit(relayArgsObj);
  };

  handleApplyRewardsToCart = (
    rewardsAmount,
    setErrors,
    resetForm,
    user,
    isUserPc,
  ) => {
    const {
      relay: { environment },
    } = this.props;
    const rewardsUser = user && user.conciergeUser ? user.conciergeUser : user;
    const userId = rewardsUser && rewardsUser.userId;
    this.setState({ applyRewardsToCartMutationLoading: true });
    const relayArgsRewardsObj = {
      environment,
      amount: rewardsAmount,
      yotpoRedemptionId,
      userId,
      onCompleted: (res1, err1) => {
        this.setState({ applyRewardsToCartMutationLoading: false });
        if (err1) {
          console.log('error in CartApplyPointsMutation', err1);
          return setErrors({ rewardAmount: err1[0].message.toString() });
        }
        this.setState({ cartPromoCodeUpdateMutationLoading: true });
        const coupon =
          res1 &&
          res1.cartApplyPoints &&
          res1.cartApplyPoints.pointRedemptionCoupon;
        const couponCode = coupon && coupon.rewardCouponCode;
        const relayArgsPromoUpdateObj = {
          environment,
          promoCodes: [couponCode],
          noGiftCards: false,
          key: isUserPc ? 'preferredCustomer' : undefined,
          onCompleted: (res2, err2) => {
            this.setState({ cartPromoCodeUpdateMutationLoading: false });
            if (err2) {
              console.log('error in CartUpdatePromoCodesMutation', err2);
              return setErrors({ rewardAmount: err2[0].message.toString() });
            }
            // remove any yotpo giftcards
            const cart =
              res2 &&
              res2.cartUpdatePromoCodes &&
              res2.cartUpdatePromoCodes.cartV2;
            const oldRewards = getOldRewardsCoupon(cart, couponCode);
            if (oldRewards) {
              const relayArgsRewardsDeleteObj = {
                environment,
                promoCode: oldRewards.code,
                onCompleted: (res3, err3) => {
                  if (err3) {
                    console.log('error in CartPromoCodeDeleteMutation', err3);
                    return setErrors({
                      rewardAmount: err3[0].message.toString(),
                    });
                  }
                  resetForm();
                },
                onError: transaction => {
                  this.setState({ cartPromoCodeUpdateMutationLoading: false });
                  console.log('TRANS in error', transaction);
                },
              };
              CartPromoCodeDeleteMutation.commit(relayArgsRewardsDeleteObj);
            } else {
              resetForm();
            }
          },
          onError: transaction => {
            this.setState({ applyRewardsToCartMutationLoading: false });
            console.log('TRANS in error', transaction);
          },
        };
        CartUpdatePromoCodesMutation.commit(relayArgsPromoUpdateObj);
      },
      onError: transaction => {
        console.log('TRANS in error', transaction);
        if (transaction.graphQLErrors[0].message) {
          return setErrors({
            rewardAmount: transaction.graphQLErrors[0].message.toString(),
          });
        }
      },
    };
    CartApplyPointsMutation.commit(relayArgsRewardsObj);
  };

  handleCheckout = e => {
    e.preventDefault();
    const {
      handleRouteChange,
      // viewer
    } = this.props;
    // const { user, cart } = viewer;
    // trackEvent({
    //   user,
    //   event: 'Checkout Started',
    //   properties: {
    //     cart,
    //   },
    // });
    handleRouteChange(e, `/checkout`);
  };

  handleAddMoreItems = e => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange(e, `/shop/wine/wine`);
  };

  handleGoToPreferred = e => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange(e, '/preferred');
  };

  render() {
    const {
      viewer,
      flags,
      cartNavOpen,
      showPwpDialog,
      handleCartClose,
      handleRouteChange,
      togglePwpDialogClosed,
    } = this.props;
    const {
      cartMutationLoading,
      applyRewardsToCartMutationLoading,
      cartPromoCodeUpdateMutationLoading,
    } = this.state;
    if (
      !viewer ||
      !viewer.user ||
      !viewer.user.cartV2 ||
      !viewer.productsBulk
    ) {
      return null;
    }
    const { user, productsBulk } = viewer;
    return (
      <ApolloProvider client={client}>
        <ApolloConsumer>
          {client => (
            <Cart
              user={user}
              flags={flags}
              pwpConfig={pwpConfig}
              cartOpen={cartNavOpen}
              productsBulk={productsBulk}
              assetsUrl={urlSquareShopProduct}
              showPwpDialog={showPwpDialog}
              hopeCommerceUrl={urlHopecommerce}
              checkoutUrl={urlCheckout}
              toggleCartOpen={handleCartClose}
              handleCartAdd={this.handleCartAdd}
              toggleCartClosed={handleCartClose}
              handleRouteChange={handleRouteChange}
              handleGoToProductDetailsPage={() => {}}
              handleGoToCheckout={this.handleCheckout}
              handleCartDelete={this.handleCartDelete}
              cartMutationLoading={cartMutationLoading}
              handleAutoshipCount={this.setAutoshipCount}
              handleAddMoreItems={this.handleAddMoreItems}
              handleGoToPreferred={this.handleGoToPreferred}
              togglePwpDialogClosed={togglePwpDialogClosed}
              handleTogglePcMembership={this.togglePCEnroll}
              handleCartAutoshipToggle={this.togglePCAutoship}
              handlePromoCodeDelete={this.handleDeletePromoCode}
              handlePromoCodeUpdate={this.handlePromoCodeUpdate}
              handleApplyRewards={this.handleApplyRewardsToCart}
              applyRewardsToCartMutationLoading={
                applyRewardsToCartMutationLoading
              }
              cartPromoCodeUpdateMutationLoading={
                cartPromoCodeUpdateMutationLoading
              }
              seasonalWineClubDateGate={seasonalWineClubDateGate}
            />
          )}
        </ApolloConsumer>
      </ApolloProvider>
    );
  }
}

CartRightNavComponent.propTypes = propTypes;

const CartFragmentComponent = createFragmentContainer(
  pure(withLDConsumer()(CartRightNavComponent)),
  {
    viewer: graphql`
      fragment CartRightNavComponent_viewer on Viewer {
        id
        productsBulk(
          first: 2
          productIds: [
            "4f3661e6-9d11-43bc-872e-6f4cc1df744f"
            "7e86bc10-4203-4078-a006-ba8394422f20"
          ]
        ) {
          edges {
            node {
              id
              productId
              price
              priceSale
              images {
                id
                productCatalog
                squareFront
              }
              productPermissions {
                oneCommerce {
                  preferredCustomer {
                    add
                  }
                }
              }
            }
          }
        }
        user {
          id
          role
          userId
          email
          firstName
          pointBalance
          permissions {
            id
            oneCommerce {
              id
              ceo {
                access
              }
              preferredCustomer {
                access
              }
            }
          }
          conciergeCustomerAccountId
          conciergeUser {
            firstName
            lastName
            userId
            pointBalance
            permissions {
              id
              oneCommerce {
                id
                ceo {
                  access
                }
                preferredCustomer {
                  access
                }
              }
            }
          }
          shoppingWithCeoUser {
            id
            userId
          }
          cartV2 {
            id
            cartId
            autoShipEnabled
            autoShipIntervalUnit
            autoShipIntervalCount
            bottleDiscount4
            bottleDiscount6
            bottleDiscount12
            promoCodeSavings
            saleSavings
            canSubscribeForAutoShip
            discountQualifiedCartTotal
            subTotalPriceOfItems
            subTotalPriceOfItemsWithDiscount
            total
            totalSavings
            totalNumberOfItems
            subTotalPriceOfItems
            eventId
            eventHostFirstName
            event {
              id
              eventId
              trinityPartyType
            }
            promoCodes {
              id
              code
              promoDescription
              thresholds {
                discountAmount
                discountAmountEach
              }
            }
            wordPressCoupons {
              code
              couponDescription
              storeBalance
              yotpoCouponCode
            }
            wordPressCouponSavings
            entries(first: 1000) {
              edges {
                node {
                  id
                  sku
                  productId
                  quantity
                  productName
                  productURLslug
                  productImageUrl
                  wineBottleQuantity
                  isWinePack
                  priceOriginal
                  priceSale
                  priceDiscount
                  discountEligible
                  priceDiscountPercent
                  hasQualifyingDiscount
                }
              }
            }
          }
        }
      }
    `,
  },
);

const noVariables = {};

// eslint-disable-next-line react/display-name
export default ({ environment, ...props }) => {
  return (
    <QueryRenderer
      variables={noVariables}
      environment={environment}
      query={graphql`
        query CartRightNavComponentQuery {
          viewer {
            id
            ...CartRightNavComponent_viewer
          }
        }
      `}
      render={({ error, props: propsFromRender }) => {
        if (error) {
          return <div />;
        }
        if (propsFromRender && propsFromRender.viewer) {
          return (
            <CartFragmentComponent {...props} viewer={propsFromRender.viewer} />
          );
        }
      }}
    />
  );
};
